/**
 * Phase 11 / Task 11.1 (DEC-120) — hauler-configurable InReview settings.
 *
 * One row per hauler tenant (tenant_id PK-unique), RLS-scoped. Controls the
 * auto-transition-to-INREVIEW behaviour on job completion:
 *   - require_inreview_for_all_tickets — when true, EVERY completed ticket
 *     enters INREVIEW (universal gate). When false (default), only tickets that
 *     fire at least one enabled flag enter INREVIEW; clean tickets skip straight
 *     to TO_BE_INVOICED.
 *   - flag_* toggles — each flag is computed ONLY when its toggle is enabled.
 *   - volume_anomaly_threshold_bbl — net-BBL ceiling for VOLUME_ANOMALY.
 *   - escalation_threshold_hours — single source of truth for Task 11.6 stale
 *     escalation (default 48). Reused by 11.6 — no separate field.
 *   - late_record_threshold_hours — DEC-352: a driver record reaching the server more
 *     than this many hours from its own time is accepted and flagged TIME_CHECK
 *     (default 24, migration 0176). It used to be rejected outright.
 *
 * The row is lazily seeded with defaults on first read (GET inreview-settings),
 * so a missing row never 500s. Settings are read at completion time only —
 * changing them never re-evaluates tickets already in INREVIEW or advanced.
 */

import { pgTable, uuid, boolean, integer, timestamp, pgPolicy, check } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';

export const tenantInreviewSettings = pgTable(
  'tenant_inreview_settings',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .unique()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    requireInreviewForAllTickets: boolean('require_inreview_for_all_tickets')
      .notNull()
      .default(false),
    flagGpsOutsideRange: boolean('flag_gps_outside_range').notNull().default(true),
    flagGpsDisposalJobTypesOnly: boolean('flag_gps_disposal_job_types_only')
      .notNull()
      .default(true),
    // DEC-319 (d) / migration 0126 — ships dark; see INREVIEW_FLAG_CODES GPS_UNVERIFIABLE.
    flagGpsUnverifiable: boolean('flag_gps_unverifiable').notNull().default(false),
    flagDispatcherCompleted: boolean('flag_dispatcher_completed').notNull().default(true),
    flagVolumeAnomaly: boolean('flag_volume_anomaly').notNull().default(true),
    volumeAnomalyThresholdBbl: integer('volume_anomaly_threshold_bbl').notNull().default(200),
    flagMissingGauge: boolean('flag_missing_gauge').notNull().default(true),
    flagMissingSignature: boolean('flag_missing_signature').notNull().default(false),
    // OPEN-121 / migration 0242 — MISSING_PHOTO; fires only on REQUIRED-photo job types.
    flagMissingPhoto: boolean('flag_missing_photo').notNull().default(true),
    // Firestone gap 10 / migration 0254 — MISSING_TRUCK; no truck on any capture of the ticket.
    flagMissingTruck: boolean('flag_missing_truck').notNull().default(true),
    // OPEN-217 / DEC-382 (migration 0266) — VALIDATION_RULE; a driver entry broke a hauler rule.
    flagValidationRule: boolean('flag_validation_rule').notNull().default(true),
    flagDuplicateTimestamps: boolean('flag_duplicate_timestamps').notNull().default(false),
    escalationThresholdHours: integer('escalation_threshold_hours').notNull().default(48),
    // DEC-352 / migration 0176.
    lateRecordThresholdHours: integer('late_record_threshold_hours').notNull().default(24),
    // #60 / DEC-365 (D8, 0189) — SITE_CHANGED routes a clean ticket into review (ON) or only
    // rides along (OFF).
    routeSiteChangeToReview: boolean('route_site_change_to_review').notNull().default(true),
    // OPEN-169 addendum / migration 0246 — Send Back to the driver. Default OFF, and the PATCH
    // route refuses turning it on until DRIVER_CORRECTION_FLOW_AVAILABLE (inreview.ts) is true.
    allowSendBack: boolean('allow_send_back').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      'tenant_inreview_settings_volume_threshold_positive_check',
      sql`${t.volumeAnomalyThresholdBbl} > 0`,
    ),
    check(
      'tenant_inreview_settings_escalation_hours_range_check',
      sql`${t.escalationThresholdHours} BETWEEN 1 AND 336`,
    ),
    check(
      'tenant_inreview_settings_late_record_hours_range_check',
      sql`${t.lateRecordThresholdHours} BETWEEN 1 AND 336`,
    ),
    pgPolicy('tenant_inreview_settings_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type TenantInreviewSettings = typeof tenantInreviewSettings.$inferSelect;
export type NewTenantInreviewSettings = typeof tenantInreviewSettings.$inferInsert;
export const tenantInreviewSettingsSelectSchema = createSelectSchema(tenantInreviewSettings);
export const tenantInreviewSettingsInsertSchema = createInsertSchema(tenantInreviewSettings);
